import * as yup from 'yup';

export const CUSTOM_SEQUENCE_DATE_PRESETS = ['YYYY-MM-DD', 'DD-MM-YYYY', 'YYYYMMDD', 'YYYY-MM', 'YYYY'];

const DATE_TOKEN = '(?:YYYY|YY|MM|M|DD|D)';
const CUSTOM_DATE_FORMAT_RE = new RegExp(`^${DATE_TOKEN}(?:[-/.]${DATE_TOKEN})*$`);

export const normalizeFolioPart = (value) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '');

export const ownFolioPrefixSegment = (folioPrefix) => {
  const raw = String(folioPrefix ?? '').trim();
  if (!raw) return '';
  const splitter = raw.includes('\\') ? /\\+/ : raw.includes('/') ? /\// : null;
  const segments = (splitter ? raw.split(splitter) : [raw])
    .map((segment) => normalizeFolioPart(segment))
    .filter(Boolean);
  return segments.length ? segments[segments.length - 1] : '';
};

export const defaultOwnFolioPrefix = (nombre) => {
  const leaf = ownFolioPrefixSegment(nombre) || String(nombre || '');
  return normalizeFolioPart(leaf).slice(0, 3);
};

export const customSequenceId = (sequence) => String(sequence?.id || sequence?._id || sequence?.prefix || '');

export const customSequenceSummary = (sequence) => {
  const components =
    Array.isArray(sequence?.components) && (sequence.components.length || Number(sequence?.formatVersion) >= 2)
      ? sequence.components
      : sequence?.prefix
        ? [{type: 'prefix', value: sequence.prefix}]
        : [];
  const labels = components.map((component) => {
    if (component.type === 'prefix') return normalizeFolioPart(component.value || sequence.prefix);
    if (component.type === 'date') return component.format || 'YYYY-MM-DD';
    if (component.type === 'location') return component.source === 'root' ? 'UBICACIÓN RAÍZ' : 'UBICACIÓN';
    return '';
  });
  return [...labels.filter(Boolean), '#'].join('-');
};

export const customSequenceOptions = (sequences = []) =>
  sequences.map((sequence) => ({
    value: customSequenceId(sequence),
    legacyValue: sequence.prefix,
    label: `${sequence.nombre} (${customSequenceSummary(sequence)})`,
  }));

export const resolveCustomSequenceValue = (sequences = [], id, legacyPrefix) => {
  if (id) return String(id);
  const normalizedPrefix = normalizeFolioPart(legacyPrefix);
  const legacyMatch = sequences.find((sequence) => normalizeFolioPart(sequence?.prefix) === normalizedPrefix);
  return legacyMatch ? customSequenceId(legacyMatch) : null;
};

export const normalizeSequenceForForm = (sequence) => {
  const legacyPrefix = normalizeFolioPart(sequence?.prefix);
  const components =
    Array.isArray(sequence?.components) && (sequence.components.length || Number(sequence?.formatVersion) >= 2)
      ? sequence.components
      : legacyPrefix
        ? [{type: 'prefix', value: legacyPrefix}]
        : [];
  return {
    ...sequence,
    formatVersion: 2,
    startNumber: Number(sequence?.startNumber ?? sequence?.nextNumber ?? 1),
    components,
    resetBy: Array.isArray(sequence?.resetBy) ? sequence.resetBy : [],
  };
};

export const createEmptyCustomSequence = () => ({
  nombre: '',
  descripcion: '',
  formatVersion: 2,
  startNumber: 1,
  components: [],
  resetBy: [],
});

const componentSchema = yup.object().shape({
  type: yup.string().oneOf(['prefix', 'date', 'location']).required(),
  value: yup.string().when('type', {
    is: 'prefix',
    then: yup.string().transform(normalizeFolioPart).required('El prefijo es requerido'),
  }),
  format: yup.string().when('type', {
    is: 'date',
    then: yup
      .string()
      .required('El formato es requerido')
      .test('date-format', 'Formato de fecha inválido', (value) => {
        const format = String(value || '').toUpperCase();
        return CUSTOM_SEQUENCE_DATE_PRESETS.includes(format) || CUSTOM_DATE_FORMAT_RE.test(format);
      }),
  }),
  source: yup.string().when('type', {
    is: 'location',
    then: yup.string().oneOf(['leaf', 'root']).required(),
  }),
  fallbackLength: yup.number().when('type', {
    is: 'location',
    then: yup.number().integer().min(1).max(20).required(),
  }),
});

export const customSequencePageSchema = yup.object().shape({
  customSequences: yup.array().of(
    yup
      .object()
      .shape({
        nombre: yup.string().trim().required('El nombre es requerido'),
        descripcion: yup.string().nullable(),
        startNumber: yup.number().integer().min(1).required('El número inicial es requerido'),
        components: yup.array().of(componentSchema),
        resetBy: yup.array().of(yup.string().oneOf(['date', 'location'])),
      })
      .test('unique-components', 'Cada componente puede agregarse una sola vez', (sequence: any) => {
        const types = (sequence?.components || []).map((component) => component.type);
        return new Set(types).size === types.length;
      }),
  ),
});
